
import { useState, useEffect, useCallback } from 'react';
import { unifiedNotificationService } from '@/services/unifiedNotificationService';
import { useUnifiedUser } from './useUnifiedUser';

export const useUnifiedNotifications = () => {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useUnifiedUser();

  const loadNotifications = useCallback(async () => {
    if (!user) {
      console.log('📭 UNIFIED: No user found for loading notifications');
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    try {
      console.log('📬 UNIFIED: Loading notifications for user:', user.username, user.id, 'Type:', user.userType);
      const userNotifications = await unifiedNotificationService.getNotifications(user);
      
      // Only update state if notifications have actually changed
      setNotifications(prevNotifications => {
        const hasChanged = prevNotifications.length !== userNotifications.length ||
          prevNotifications.some((prev, index) => 
            !userNotifications[index] || 
            prev.id !== userNotifications[index].id ||
            prev.is_read !== userNotifications[index].is_read
          );
        
        if (hasChanged) {
          console.log('📬 UNIFIED: Notifications changed, updating state:', userNotifications.length);
          return userNotifications;
        } else {
          console.log('📬 UNIFIED: No changes detected, keeping existing state');
          return prevNotifications;
        }
      });
      
      const unread = userNotifications.filter(notification => !notification.is_read).length;
      setUnreadCount(prevUnread => {
        if (prevUnread !== unread) {
          console.log('🔔 UNIFIED: Unread count changed from', prevUnread, 'to', unread, 'for user:', user.username);
          return unread;
        }
        return prevUnread;
      });
      
    } catch (error) {
      console.error('❌ UNIFIED: Error loading notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user]);

  // Optimized event listeners to prevent redundant fetching
  useEffect(() => {
    if (!user) return;

    // Debounced notification reload to prevent excessive API calls
    let reloadTimeout: NodeJS.Timeout;
    const debouncedReload = () => {
      clearTimeout(reloadTimeout);
      reloadTimeout = setTimeout(() => {
        console.log('🔔 UNIFIED: Debounced notification reload');
        loadNotifications();
      }, 300);
    };

    const handleNotificationEvents = (event: Event) => {
      console.log('🔔 UNIFIED: Notification event received:', event.type);
      debouncedReload();
    };

    // Reduced event types to prevent redundancy
    const eventTypes = [
      'unified-notification',
      'notification-update',
      'enhanced-notification-trigger'
    ];

    // Only add listeners to window to prevent duplicate events
    eventTypes.forEach(eventType => {
      window.addEventListener(eventType, handleNotificationEvents);
    });

    // Storage change listener for cross-tab updates
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'user_notifications') {
        console.log('💾 UNIFIED: Storage change detected for notifications');
        debouncedReload();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Initial load only once
    loadNotifications();

    // Reduced periodic check frequency to prevent excessive API calls
    const interval = setInterval(() => {
      console.log('🔄 UNIFIED: Periodic notification check for', user?.username);
      loadNotifications();
    }, 30000); // Increased to 30 seconds

    return () => {
      clearTimeout(reloadTimeout);
      eventTypes.forEach(eventType => {
        window.removeEventListener(eventType, handleNotificationEvents);
      });
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [user, loadNotifications]);

  const markAsRead = useCallback(async (notificationId: string, isGameNotification: boolean = false) => {
    if (!user) return;

    try {
      console.log('📖 UNIFIED: Marking notification as read:', notificationId, 'Game notification:', isGameNotification);
      await unifiedNotificationService.markAsRead(notificationId, isGameNotification);
      await loadNotifications(); // Reload to update state
    } catch (error) {
      console.error('❌ UNIFIED: Error marking notification as read:', error);
    }
  }, [user, loadNotifications]);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;

    try {
      console.log('📖 UNIFIED: Marking all notifications as read for:', user.username);
      await unifiedNotificationService.markAllAsRead(user);
      await loadNotifications(); // Reload to update state
    } catch (error) {
      console.error('❌ UNIFIED: Error marking all notifications as read:', error);
    }
  }, [user, loadNotifications]);

  // Reduce console logging to prevent spam
  if (process.env.NODE_ENV === 'development') {
    console.log('=== UNIFIED NOTIFICATIONS HOOK STATE ===');
    console.log('User:', user?.username, 'Type:', user?.userType);
    console.log('Current notifications count:', notifications.length);
    console.log('Unread count:', unreadCount);
  }

  return {
    notifications,
    unreadCount,
    loadNotifications,
    markAsRead,
    markAllAsRead
  };
};
